import { useState, useRef, useEffect } from "react";
import { verdictAPI } from "@/lib/api";
import { Spinner, X, PaperPlaneRight, CheckCircle } from "@phosphor-icons/react";

export default function CorrectListingModal({ verdict, onClose, onCorrected }) {
  const [messages, setMessages] = useState([
    {
      role: "ai",
      text: `I see this is a **${verdict.brand || "Unknown"} ${verdict.category || "item"}** — "${verdict.title_keywords}". What needs to be corrected?`,
    },
  ]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [corrected, setCorrected] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight);
  }, [messages]);

  const handleSend = async () => {
    const msg = input.trim();
    if (!msg || sending) return;

    setMessages((prev) => [...prev, { role: "user", text: msg }]);
    setInput("");
    setSending(true);

    try {
      const { data } = await verdictAPI.correct(verdict.id, msg);

      if (data.corrections && Object.keys(data.corrections).length > 0) {
        const changes = Object.entries(data.corrections)
          .filter(([k]) => !["corrected_at", "correction_note"].includes(k))
          .map(([k, v]) => `**${k}**: ${v}`)
          .join("\n");

        setMessages((prev) => [
          ...prev,
          {
            role: "ai",
            text: `Got it! I've updated:\n${changes}\n\nAnything else to fix?`,
          },
        ]);
        setCorrected(true);
        if (onCorrected && data.verdict) {
          onCorrected(data.verdict);
        }
      } else {
        setMessages((prev) => [
          ...prev,
          { role: "ai", text: "I didn't find any changes to make based on that. Can you be more specific about what's wrong?" },
        ]);
      }
    } catch {
      setMessages((prev) => [
        ...prev,
        { role: "ai", text: "Sorry, I couldn't process that correction. Please try again." },
      ]);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 flex items-end sm:items-center justify-center pb-[72px] sm:pb-0" data-testid="correct-listing-modal">
      <div className="bg-white border-t-4 sm:border-4 border-black w-full sm:max-w-md sm:shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] max-h-[80vh] sm:max-h-[70vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b-2 border-black shrink-0">
          <h2 className="font-heading text-base font-black uppercase tracking-tight">Correct This Listing</h2>
          <button onClick={onClose} className="p-1" data-testid="close-correct-modal">
            <X size={20} weight="bold" />
          </button>
        </div>

        {/* Messages */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
          {messages.map((msg, i) => (
            <div
              key={i}
              className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
            >
              <div
                className={`max-w-[85%] p-3 font-body text-sm whitespace-pre-wrap ${
                  msg.role === "user"
                    ? "bg-[#002FA7] text-white border-2 border-black"
                    : "bg-neutral-100 text-black border-2 border-neutral-200"
                }`}
              >
                {msg.text.split("**").map((part, j) =>
                  j % 2 === 1 ? (
                    <strong key={j}>{part}</strong>
                  ) : (
                    <span key={j}>{part}</span>
                  )
                )}
              </div>
            </div>
          ))}
          {sending && (
            <div className="flex justify-start">
              <div className="p-3 bg-neutral-100 border-2 border-neutral-200 flex items-center gap-2">
                <Spinner size={14} weight="bold" className="animate-spin" />
                <span className="font-body text-xs text-neutral-500">Updating...</span>
              </div>
            </div>
          )}
        </div>

        {/* Input */}
        <div className="p-3 border-t-2 border-black shrink-0">
          {corrected && (
            <div className="flex items-center gap-1.5 mb-2 text-green-700">
              <CheckCircle size={14} weight="bold" />
              <span className="font-body text-xs font-semibold">Listing updated</span>
            </div>
          )}
          <div className="flex gap-2">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSend()}
              placeholder='e.g. "The brand is actually Adidas"'
              className="flex-1 p-2 border-2 border-black font-body text-sm"
              disabled={sending}
              data-testid="correct-input"
            />
            <button
              onClick={handleSend}
              disabled={sending || !input.trim()}
              className="p-2 bg-black text-white border-2 border-black disabled:opacity-30"
              data-testid="correct-send-btn"
            >
              <PaperPlaneRight size={18} weight="bold" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
